/**
 * useRoleLanding — single source of truth for role-based landing paths.
 *
 * Why this hook exists (NEXT_SESSION P3 governance debt)
 * ------------------------------------------------------
 * Worker landing fix (HANDOFF_2026-05-15) shipped four independent
 * places that each read ``getCurrentRoleCode()`` and re-derived "if
 * worker → /me/dashboard else /dashboard":
 *
 *   1. ``App.tsx`` ``RoleRedirect``        — top-level Navigate
 *   2. ``MainLayout.tsx``                  — empty-path fallback
 *   3. ``OrgGate.tsx``                     — post-pick redirect (×2)
 *   4. ``WorkerPortal/WorkerLayout.tsx``   — manager role gate
 *
 * Adding a new role (e.g. an "auditor" landing page) meant touching
 * all four sites and trusting code review to catch the missing one.
 * This module collapses the logic into one helper plus a hook
 * wrapper, so callers depend on the role-routing contract instead of
 * re-implementing it.
 *
 * Usage in a React component:
 *
 *   const { isWorker, landingPath, resolveLandingPath, isManager }
 *     = useRoleLanding();
 *
 *   if (someCondition) navigate(landingPath);
 *
 * Usage in a callback (non-React-tree, e.g. event handler that lives
 * inside a useCallback) — call the bare functions:
 *
 *   const dest = resolveRoleLanding(role, intendedPath);
 *
 * Both surfaces read from ``localStorage`` via ``getCurrentRoleCode``.
 */
import { useMemo } from 'react';

import { getCurrentRoleCode } from '../api/client';

// ---------------------------------------------------------------------------
// Constants — the only place these strings should appear.
// ---------------------------------------------------------------------------

/** Worker portal home. Locked to ``/me/dashboard`` so all worker
 *  pages live under the ``/me/*`` prefix and the ``WorkerLayout`` is
 *  the sole shell. Update this if the worker portal moves. */
export const WORKER_LANDING = '/me/dashboard';

/** Admin / generic-staff dashboard. Locked to ``/dashboard``. Anyone
 *  whose role isn't ``worker`` defaults to here — including new roles
 *  we haven't taught the portal about yet. */
export const ADMIN_LANDING = '/dashboard';
export const ADMIN_SECURITY_PATH = '/account/security';
export const WORKER_SECURITY_PATH = '/me/security';

/** Roles that retain manager-tier nav inside the worker portal
 *  (e.g. seeing /me/team and /me/approvals). 'admin' and 'super_admin'
 *  are included even though they don't normally land in the worker
 *  portal — if they navigate there manually, they should still see
 *  the full nav. */
const MANAGER_ROLES = new Set([
  'manager', 'hr_manager', 'admin', 'super_admin',
]);

/** Admin-tier roles land in the ADMIN shell (`/dashboard`). EVERYONE else —
 *  worker, `manager` (a team lead who still clocks in), and any unknown/future
 *  non-privileged role — lands in the worker portal (`/me/*`).
 *
 *  This is the ADR-031 fix for the role-routing drift: web previously sent any
 *  non-`worker` role to the admin shell, ejecting `manager` team-leads from the
 *  portal into a ~7-permission empty admin view AND stripping their clock-in.
 *  Within-portal manager surfaces (Team / Approvals) are gated by oversight via
 *  `useRoleProfile` — NOT by role_code — so this only decides the LANDING. */
const ADMIN_TIER_ROLES = new Set([
  'super_admin', 'admin', 'hr_manager', 'owner', 'org_admin',
]);

export function isAdminTierRole(role: string | null | undefined): boolean {
  return !!role && ADMIN_TIER_ROLES.has(role);
}


/** A membership without a role has no portal access and must stay in OrgGate. */
export function hasPortalRoleCode(
  role: string | null | undefined,
): role is string {
  return typeof role === 'string' && role.length > 0;
}


// ---------------------------------------------------------------------------
// Pure helpers — usable from non-component callbacks.
// ---------------------------------------------------------------------------

export function filterPortalOrganizations<
  T extends { role_code?: string | null },
>(organizations: T[]): Array<T & { role_code: string }> {
  return organizations.filter(
    (organization): organization is T & { role_code: string } =>
      hasPortalRoleCode(organization.role_code),
  );
}

/** True only for the canonical frontline worker role. Portal landing for
 *  manager/unknown roles is decided separately by landingPathFor(). */
export function isWorkerRole(role: string | null | undefined): boolean {
  return role === 'worker';
}

/** Default landing path for a given role. Admin-tier → admin shell; everyone
 *  else (worker, manager team-lead, unknown) → worker portal. */
export function landingPathFor(role: string | null | undefined): string {
  return isAdminTierRole(role) ? ADMIN_LANDING : WORKER_LANDING;}

function pathOnly(path: string): string {
  return path.split(/[?#]/, 1)[0] || '/';
}

function isWorkerPortalPath(path: string): boolean {
  const pathname = pathOnly(path);
  return pathname === '/me' || pathname.startsWith('/me/');
}

function isAdminPortalPath(path: string): boolean {
  const pathname = pathOnly(path);
  return [
    '/dashboard',
    '/account',
    '/organization',
    '/processes',
    '/attendance',
    '/integrations',
    '/system',
  ].some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
}

/** Reconcile a deep link with the selected organization role. Admin roles
 *  never enter the worker shell; worker/manager roles never enter an admin
 *  module. Query strings and hashes are preserved when the target is valid. */
export function resolveRoleLanding(
  role: string | null | undefined,
  intended: string | null | undefined,
): string {
  if (!intended) return landingPathFor(role);
  if (isAdminTierRole(role) && isWorkerPortalPath(intended)) {
    return ADMIN_LANDING;
  }
  if (!isAdminTierRole(role) && isAdminPortalPath(intended)) {
    return WORKER_LANDING;
  }
  return intended;
}

export function securityPathFor(role: string | null | undefined): string {
  return isAdminTierRole(role) ? ADMIN_SECURITY_PATH : WORKER_SECURITY_PATH;
}


// ---------------------------------------------------------------------------
// OrgGate decision
// ---------------------------------------------------------------------------

/** What OrgGate should do once /me/organizations has answered. */
export type OrgGateDecision =
  | { mode: 'create' }
  | { mode: 'select'; organizations: Array<{ role_code: string }> }
  | { mode: 'enter'; organization: { role_code: string }; landing: string };

/**
 * Decide OrgGate's next step from the membership list.
 *
 * Extracted from `OrgGate.bootstrap` so the branch a brand-new user takes is
 * testable without mounting the whole page (i18n + LocationPicker + auth
 * context). The behaviour is unchanged:
 *
 *   * no membership that grants portal access -> the create-organization form
 *     (this is the "authorize them to create an organization" case)
 *   * exactly one, and the user is not explicitly managing -> enter it, landed
 *     by role (`worker` -> the worker portal, admin-tier -> the admin shell)
 *   * more than one, or `?manage=1` -> the picker
 *
 * Memberships whose `role_code` grants no portal access are filtered out
 * FIRST, so a user who only holds such a membership is treated as having none
 * rather than being dropped into a portal they cannot use.
 */
export function decideOrgGate<T extends { role_code?: string | null }>(
  organizations: T[],
  options?: { forceManage?: boolean; intended?: string | null },
): OrgGateDecision {
  const accessible = filterPortalOrganizations(organizations);
  if (accessible.length === 0) return { mode: 'create' };
  if (accessible.length === 1 && !options?.forceManage) {
    const organization = accessible[0];
    return {
      mode: 'enter',
      organization,
      landing: resolveRoleLanding(organization.role_code, options?.intended),
    };
  }
  return { mode: 'select', organizations: accessible };
}


// ---------------------------------------------------------------------------
// React hook — convenient inside components.
// ---------------------------------------------------------------------------

export interface RoleLanding {
  /** Raw role code from localStorage — use sparingly; prefer
   *  ``isWorker`` / ``isManager`` for branching. */
  roleCode: string | null;
  /** Convenience flag — role === 'worker'. */
  isWorker: boolean;
  /** Convenience flag — role is in MANAGER_ROLES (covers manager,
   *  hr_manager, admin, super_admin). */
  isManager: boolean;
  /** Default landing path for this role. */
  landingPath: string;
  /** Personal/admin security destination for this role's portal. */
  securityPath: string;
  /** Maps an intended deep-link to the role-correct path. */
  resolveLandingPath: (intended: string | null | undefined) => string;
}

export function useRoleLanding(): RoleLanding {
  // localStorage reads are sync; useMemo just stabilises the object
  // identity so consumers can safely include it in dep arrays.
  return useMemo(() => {
    const roleCode = getCurrentRoleCode();
    return {
      roleCode,
      isWorker: isWorkerRole(roleCode),
      isManager: !!roleCode && MANAGER_ROLES.has(roleCode),
      landingPath: landingPathFor(roleCode),
      securityPath: securityPathFor(roleCode),
      resolveLandingPath: (intended) => resolveRoleLanding(roleCode, intended),
    };
  }, []);
}
